import { z } from 'zod';
import {
  AuthorizationStates,
  type EventSchema,
  EventTypes,
  LeaderboardType,
  MapType,
  ModulesStates,
  PlayerOptionsSchema,
  SponsorLogoSchema,
  SponsorModuleSchema,
} from '../schema/events';
import { ISODateStringSchema, ObjectIdHexStringSchema, ObjectIdSchema } from './base';
import { TagsSchema } from './starters';
import { DeviceClasses } from './trackers';

export type BaseEvent = z.infer<typeof EventSchema>;

/**
 * The only starter fields the player reads. Everything else the starter
 * aggregation produces is dropped on the way out — starters dominate the
 * `/api/player/:eventId/info` payload, so this is where the bytes are.
 */
const PlayerStarterSchema = z.object({
  id: ObjectIdHexStringSchema,
  name: z.string().nullable(),
  startNumber: z.string().nullable(),
  markerColor: z.string().nullable(),
  deviceClass: z.enum(DeviceClasses).nullable(),
  tags: TagsSchema,
});

export type PlayerStarter = z.infer<typeof PlayerStarterSchema>;

/**
 * Sponsor logos are a mongoose subdocument array, so each one is stored with an
 * auto `_id` and no `id` at all. `Filters.eventFilter` used to do this rename on
 * its way out; the player route builds its payload straight from the document,
 * so the rename belongs here — where the wire shape is defined.
 */
const PlayerSponsorLogoSchema = z.preprocess((logo) => {
  if (logo == null || typeof logo !== 'object') return logo;

  const { _id, ...rest } = logo as Record<string, unknown>;
  const id = rest.id ?? _id;
  return id == null ? rest : { ...rest, id: String(id) };
}, SponsorLogoSchema);

/**
 * The modules the player reads, and only the parts of them it reads.
 * Not derived from `OptionModulesSchema`: that one carries predictive params,
 * dataFeed, registration, monitor and banner, and types `netReplay.splitId`
 * as a BSON ObjectId — none of which survive the trip to a browser.
 */
const PlayerModulesSchema = z.object({
  timing: z.object({
    state: z.enum(ModulesStates),
    leaderboard: z.object({
      type: z.enum(LeaderboardType),
      externalLeaderboardUrl: z.string(),
    }),
  }),
  sponsor: SponsorModuleSchema.extend({
    logos: z.array(PlayerSponsorLogoSchema).nullable(),
  }),
  netReplay: z.object({
    enabled: z.boolean(),
    // The model stores a String, but the document type declares an ObjectId —
    // accept either and hand the player a plain string. Stays looser than
    // ObjectIdHexStringSchema because legacy events carry '' as well as null.
    splitId: z
      .union([z.string(), ObjectIdSchema])
      .nullable()
      .transform((value) => (value == null ? null : String(value))),
    sliderDuration: z.number(),
  }),
  projection: z.object({ enabled: z.boolean() }),
});

/** Tracks and POIs carry either a CSS colour string or a legacy [r,g,b,a] tuple. */
const FeatureColorSchema = z.union([z.string(), z.array(z.number()).min(3).max(4)]).nullish();

/** Geo documents still carry raw BSON ObjectIds; the player wants hex strings. */
const FeatureIdSchema = z
  .union([z.string(), ObjectIdSchema])
  .optional()
  .transform((value) => (value == null ? undefined : String(value)));

/**
 * A point the player may draw on the map. `racemapType` is 'poi' for a plain
 * point of interest and 'split' for a timing split.
 *
 * `geometry` is null for a split the player must not draw — hidden, unnamed, or
 * with no matching shadowtrack coordinate. Those still have to be sent, because
 * parseNetReplay resolves its sync split by id, and an unlocated feature is
 * valid GeoJSON that the player's POI filter skips on its own.
 */
const PlayerPoiFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: FeatureIdSchema,
  geometry: z
    .object({
      type: z.literal('Point'),
      coordinates: z.tuple([z.number(), z.number(), z.number()]),
    })
    .nullable(),
  properties: z.object({
    racemapType: z.enum(['poi', 'split']),
    // The player prefers properties.id and falls back to the feature id.
    id: FeatureIdSchema,
    name: z.string().nullish(),
    title: z.string().nullish(),
    shortName: z.string().nullish(),
    icon: z.string().nullish(),
    color: FeatureColorSchema,
    offset: z.number().nullish(),
  }),
});

/**
 * Hidden tracks never reach the player, so `hidden` is not on the wire.
 * Neither is `properties.splits`: getLegacyFormat attaches the full split list
 * to every track and nothing reads it.
 */
const PlayerTrackFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: FeatureIdSchema,
  geometry: z.object({
    type: z.literal('LineString'),
    coordinates: z.array(z.tuple([z.number(), z.number(), z.number()])).min(2),
  }),
  properties: z.object({
    racemapType: z.literal('track'),
    id: FeatureIdSchema,
    name: z.string().nullish(),
    color: FeatureColorSchema,
    lineWidth: z.number().nullish(),
  }),
});

const PlayerExternalLayerFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: FeatureIdSchema,
  geometry: z.null(),
  properties: z.object({
    racemapType: z.literal('externalLayer'),
    layerType: z.string(),
    tiles: z.array(z.string()),
    tileSize: z.number().nullish(),
  }),
});

const PlayerFeatureSchema = z.union([
  PlayerPoiFeatureSchema,
  PlayerTrackFeatureSchema,
  PlayerExternalLayerFeatureSchema,
]);

export type PlayerFeature = z.infer<typeof PlayerFeatureSchema>;
export type PlayerPoiFeature = z.infer<typeof PlayerPoiFeatureSchema>;
export type PlayerTrackFeature = z.infer<typeof PlayerTrackFeatureSchema>;
export type PlayerExternalLayerFeature = z.infer<typeof PlayerExternalLayerFeatureSchema>;

/**
 * The wire contract of `GET /api/player/:eventId/info`.
 *
 * Deliberately NOT derived from `EventSchema`: that schema describes the
 * database document, so its inferred types are BSON ObjectIds and `Date`s,
 * which is not what a browser ever receives. This one describes the JSON on
 * the wire — hex strings and ISO strings — so the server validates and the
 * player types against the exact same shape.
 *
 * Every field here is read by `parseEvent` in
 * gears-frontend/src/lib/player/parse.ts. Nothing else belongs in it.
 */
export const PlayerEventInfoSchema = z.object({
  id: ObjectIdHexStringSchema,
  name: z.string(),
  slug: z.string().optional(),
  startTime: ISODateStringSchema.nullable(),
  endTime: ISODateStringSchema.nullable(),
  isRepeating: z.boolean(),
  eventDescription: z.string(),
  eventWebsite: z.string(),
  eventContactMail: z.string(),
  location: z.string(),
  updatedAt: ISODateStringSchema,
  startersCount: z.number(),
  shadowTrackIsVisible: z.boolean(),
  geo: z.object({
    shadowgeojsonHash: z.string().nullable(),
    /** Identifies which of the track features is the shadowtrack. */
    shadowTrackId: z.string().nullable().default(null),
  }),
  playerOptions: PlayerOptionsSchema.omit({
    breakTimeout: true,
    version: true,
    mappingDistance: true,
  }),
  modules: PlayerModulesSchema,
  starters: z.array(PlayerStarterSchema),
  geoJSON: z.object({
    type: z.string(),
    features: z.array(PlayerFeatureSchema),
  }),
});

export type PlayerEventInfo = z.infer<typeof PlayerEventInfoSchema>;

export const EventInfoSchema = z.array(PlayerEventInfoSchema);

export type EventInfo = z.infer<typeof EventInfoSchema>;

export const PlayerBaseOptionsSchema = z.object({
  eventId: z.string(),
  name: z.string(),
  slug: z.string().optional(),
  startTime: z.string(),
  endTime: z.string(),
  type: z.enum(EventTypes),
  mapType: z.enum(MapType),
  app: z.boolean(),
  authorization: z.enum(AuthorizationStates),
  baseColor: z.string(),
  largeMode: z.boolean(),
  updatedAt: z.string(),
  createdAt: z.string(),
});

export type PlayerBaseOptions = z.infer<typeof PlayerBaseOptionsSchema>;
